/**
 * Assignment Center Dashboard
 * 
 * The operational hub for managing caregiver-client assignments.
 * Supports Smart Matching and Direct Assignment workflows.
 * 
 * Philosophy: "AI Assists. People Decide."
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Users, UserPlus, Clock, FileSignature, CheckCircle, XCircle,
  AlertTriangle, Search, Filter, Plus, ChevronRight, Target,
  ClipboardCheck, UserCheck, Timer
} from "lucide-react";
import AppShell from "../../components/layout/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  needs_assignment: {
    label: "Needs Assignment",
    icon: UserPlus,
    color: "bg-blue-100 text-blue-800 border-blue-200",
    description: "Clients ready for caregiver assignment",
  },
  pending_offer: {
    label: "Pending Offers",
    icon: Clock,
    color: "bg-slate-100 text-slate-800 border-slate-200",
    description: "Offers being prepared",
  },
  awaiting_response: {
    label: "Awaiting Response",
    icon: Timer,
    color: "bg-amber-100 text-amber-800 border-amber-200",
    description: "Waiting for caregiver to respond",
  },
  awaiting_signature: {
    label: "Awaiting Signature",
    icon: FileSignature,
    color: "bg-purple-100 text-purple-800 border-purple-200",
    description: "Ready for electronic signature",
  },
  supervisor_review: {
    label: "Supervisor Review",
    icon: ClipboardCheck,
    color: "bg-orange-100 text-orange-800 border-orange-200",
    description: "Requires supervisor approval",
  },
  active: {
    label: "Active",
    icon: CheckCircle,
    color: "bg-green-100 text-green-800 border-green-200",
    description: "Active assignments",
  },
  declined: {
    label: "Declined",
    icon: XCircle,
    color: "bg-red-100 text-red-800 border-red-200",
    description: "Caregiver declined the offer",
  },
  expired: {
    label: "Expired",
    icon: AlertTriangle,
    color: "bg-gray-100 text-gray-600 border-gray-200",
    description: "Offer expired without response",
  },
};

export default function AssignmentCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [summary, setSummary] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("status") || "all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch dashboard summary
      const summaryRes = await fetch(`${API}/api/assignment-center/dashboard`, {
        credentials: "include",
      });
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
      
      // Fetch assignments
      const params = new URLSearchParams();
      if (activeTab !== "all") {
        params.append("status", activeTab);
      }
      params.append("limit", "100");
      
      const assignmentsRes = await fetch(
        `${API}/api/assignment-center/assignments?${params.toString()}`,
        { credentials: "include" }
      );
      if (assignmentsRes.ok) {
        const data = await assignmentsRes.json();
        setAssignments(data.assignments || []);
      }
    } catch (err) {
      console.error("Failed to fetch assignment center data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", value);
    }
    setSearchParams(searchParams);
  };

  // Filter by search
  const filteredAssignments = assignments.filter((a) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      a.client_name?.toLowerCase().includes(searchLower) ||
      a.caregiver_name?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleDateString();
  };

  const formatTimeAgo = (isoString) => {
    if (!isoString) return "";
    const now = new Date();
    const then = new Date(isoString);
    const diffMs = now - then;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return "Just now";
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="assignment-center">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Target className="w-6 h-6" />
              Assignment Center
            </h1>
            <p className="text-muted-foreground">
              Match caregivers to clients. AI Assists. People Decide.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/assignment-center/smart-match">
              <Button data-testid="smart-match-btn">
                <Target className="w-4 h-4 mr-2" />
                Smart Match
              </Button>
            </Link>
            <Link to="/assignment-center/direct-assign">
              <Button variant="outline" data-testid="direct-assign-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Direct Assign
              </Button>
            </Link>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {[
              { key: "needs_assignment", value: summary.needs_assignment },
              { key: "pending_offer", value: summary.pending_offers },
              { key: "awaiting_response", value: summary.awaiting_response },
              { key: "awaiting_signature", value: summary.awaiting_signature },
              { key: "supervisor_review", value: summary.supervisor_review },
              { key: "active", value: summary.active },
              { key: "declined", value: summary.declined_recent, suffix: "(7d)" },
              { key: "expired", value: summary.expired_recent, suffix: "(7d)" },
            ].map(({ key, value, suffix }) => {
              const config = STATUS_CONFIG[key];
              if (!config) return null;
              const Icon = config.icon;
              
              return (
                <Card 
                  key={key}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${
                    activeTab === key ? "ring-2 ring-primary" : ""
                  }`}
                  onClick={() => handleTabChange(key)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <Icon className="w-5 h-5 text-muted-foreground" />
                      <span className={`text-2xl font-bold ${value > 0 ? "" : "text-muted-foreground"}`}>
                        {value || 0}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {config.label} {suffix || ""}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by client or caregiver name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="assignment-search"
            />
          </div>
          <Select value={activeTab} onValueChange={handleTabChange}>
            <SelectTrigger className="w-[200px]" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignments</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Assignments List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            Loading assignments...
          </div>
        ) : filteredAssignments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">
                {activeTab === "all"
                  ? "No assignments found"
                  : `No ${STATUS_CONFIG[activeTab]?.label.toLowerCase() || ""} assignments`}
              </p>
              <div className="flex justify-center gap-2">
                <Link to="/assignment-center/smart-match">
                  <Button>
                    <Target className="w-4 h-4 mr-2" />
                    Start Smart Match
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredAssignments.map((assignment) => {
              const statusConfig = STATUS_CONFIG[assignment.status] || {};
              const StatusIcon = statusConfig.icon || Clock;
              
              return (
                <Card
                  key={assignment.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/assignment-center/${assignment.id}`)}
                  data-testid={`assignment-card-${assignment.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {/* Status Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          statusConfig.color?.split(" ")[0] || "bg-gray-100"
                        }`}>
                          <StatusIcon className={`w-5 h-5 ${
                            statusConfig.color?.split(" ")[1] || "text-gray-600"
                          }`} />
                        </div>
                        
                        {/* Info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">{assignment.client_name}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="font-medium text-primary">
                              {assignment.caregiver_name || "Unassigned"}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                            <Badge variant="outline" className={statusConfig.color}>
                              {statusConfig.label || assignment.status}
                            </Badge>
                            <span>
                              {assignment.assignment_type === "smart_match" ? "Smart Match" : "Direct Assign"}
                            </span>
                            {assignment.match_score && (
                              <span className="text-green-600 font-medium">
                                {assignment.match_score}% match
                              </span>
                            )}
                            <span>{formatTimeAgo(assignment.updated_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {assignment.status === "awaiting_response" && assignment.offer_expires_at && (
                          <Badge variant="outline" className="text-amber-600">
                            Expires {formatDate(assignment.offer_expires_at)}
                          </Badge>
                        )}
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
